import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDashboard, getStats, DashboardData, StatsData } from '../adminApi';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminDashboardPage() {
  const { admin, logout, isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    Promise.all([
      getDashboard().then(setData).catch(() => setData(null)),
      getStats().then(setStats).catch(() => setStats(null)),
    ]).finally(() => setLoading(false));
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const statCards = [
    { label: 'Пользователи', value: data?.totalUsers ?? '—', icon: '👥', color: 'from-blue-600 to-blue-800' },
    { label: 'Новые сегодня', value: data?.newUsersToday ?? '—', icon: '🆕', color: 'from-green-600 to-green-800' },
    { label: 'Подписки', value: data?.activeSubscriptions ?? '—', icon: '💎', color: 'from-purple-600 to-purple-800' },
    { label: 'Доход сегодня', value: data?.revenueToday ? `${data.revenueToday} ₽` : '—', icon: '💰', color: 'from-amber-600 to-amber-800' },
  ];

  const extendedStats = [
    { label: 'Конверсия в подписку', value: stats?.conversionRate ? `${stats.conversionRate}%` : '—', icon: '📈' },
    { label: 'Триалы активные', value: stats?.activeTrials ?? '—', icon: '⏳' },
    { label: 'Истёкшие триалы', value: stats?.expiredTrials ?? '—', icon: '⚠️' },
    { label: 'Средний чек', value: stats?.avgRevenue ? `${stats.avgRevenue} ₽` : '—', icon: '💵' },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold text-white">Astranum Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">{admin?.email} ({admin?.role})</span>
            <Link
              to="/admin/users"
              className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
            >
              Пользователи
            </Link>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/40 transition"
            >
              Выход
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`bg-gradient-to-br ${card.color} rounded-xl p-6 shadow-lg`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">{card.label}</p>
                  <p className="text-white text-3xl font-bold mt-1">{card.value}</p>
                </div>
                <span className="text-4xl">{card.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Extended Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {extendedStats.map((card) => (
            <div
              key={card.label}
              className="bg-slate-800 border border-slate-700 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{card.label}</p>
                  <p className="text-white text-2xl font-bold mt-1">{card.value}</p>
                </div>
                <span className="text-3xl">{card.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/admin/users" className="bg-slate-800 rounded-xl border border-slate-700 p-4 hover:bg-slate-700/50 transition text-center">
            <div className="text-2xl mb-1">👥</div>
            <div className="text-white text-sm font-medium">Пользователи</div>
          </Link>
          <Link to="/admin/subscriptions" className="bg-slate-800 rounded-xl border border-slate-700 p-4 hover:bg-slate-700/50 transition text-center">
            <div className="text-2xl mb-1">💎</div>
            <div className="text-white text-sm font-medium">Подписки</div>
          </Link>
          <Link to="/admin/payments" className="bg-slate-800 rounded-xl border border-slate-700 p-4 hover:bg-slate-700/50 transition text-center">
            <div className="text-2xl mb-1">💳</div>
            <div className="text-white text-sm font-medium">Платежи</div>
          </Link>
          <Link to="/admin/broadcasts" className="bg-slate-800 rounded-xl border border-slate-700 p-4 hover:bg-slate-700/50 transition text-center">
            <div className="text-2xl mb-1">📢</div>
            <div className="text-white text-sm font-medium">Рассылки</div>
          </Link>
        </div>

        {/* Recent Registrations */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Последние регистрации</h2>
          </div>
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="px-6 py-3 font-medium">Email</th>
                    <th className="px-6 py-3 font-medium">Имя</th>
                    <th className="px-6 py-3 font-medium">Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recentRegistrations?.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                        Нет регистраций
                      </td>
                    </tr>
                  ) : (
                    data?.recentRegistrations?.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-slate-700/50 hover:bg-slate-700/30 transition"
                      >
                        <td className="px-6 py-3 text-white">
                          <Link to={`/admin/users/${user.id}`} className="text-blue-400 hover:underline">
                            {user.email}
                          </Link>
                        </td>
                        <td className="px-6 py-3 text-slate-300">{user.full_name || '—'}</td>
                        <td className="px-6 py-3 text-slate-400">
                          {new Date(user.created_at).toLocaleDateString('ru-RU')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
